import { useMemo } from "react";

interface IUseCalendarViewDaysParams {
  numberOfDays: number;
  selectedDate?: Date | null;
  studioHours?: {
    [key: string]: {
      slots: Array<{ startTime: string; endTime: string }>;
      isWorkingDay: boolean;
    };
  } | null;
  hasRealData: boolean;
  hasAvailableSlots?: (date: Date) => boolean;
}

export interface ICalendarDayItem {
  kind: "day";
  key: string;
  date: Date;
  day: number;
  isDisabled: boolean;
  isSelected: boolean;
  hasSlots: boolean;
}

export interface ICalendarBannerItem {
  kind: "banner";
  key: string;
  label: string;
}

export interface ICalendarEmptyItem {
  kind: "empty";
  key: string;
}

export type CalendarViewItem =
  | ICalendarDayItem
  | ICalendarBannerItem
  | ICalendarEmptyItem;

const DAYS_PER_WEEK = 7;

const formatBannerLabel = (date: Date): string =>
  date.toLocaleDateString("bg-BG", { month: "long" });

export const useCalendarViewDays = ({
  numberOfDays,
  selectedDate,
  studioHours,
  hasRealData,
  hasAvailableSlots,
}: IUseCalendarViewDaysParams): CalendarViewItem[] => {
  return useMemo(() => {
    const today = new Date();
    const items: CalendarViewItem[] = [];
    let lastMonthKey: string | null = null;

    for (let i = 0; i < numberOfDays; i++) {
      const baseDate = new Date(today);
      baseDate.setDate(today.getDate() + i);
      const dateKey = baseDate.toISOString().split("T")[0];
      const date = new Date(dateKey + "T12:00:00");

      const dayOfWeek = date.getDay();
      const isWorkingDay =
        hasRealData && studioHours
          ? studioHours[dayOfWeek.toString()]?.isWorkingDay || false
          : true;

      const hasSlots = hasAvailableSlots
        ? hasAvailableSlots(date)
        : isWorkingDay;

      const isDisabled = !hasSlots;
      const isSelected = selectedDate
        ? date.toDateString() === selectedDate.toDateString()
        : false;

      const monthKey = `${date.getFullYear()}-${date.getMonth()}`;
      const isNewMonth = monthKey !== lastMonthKey;

      if (isNewMonth) {
        items.push({
          kind: "banner",
          key: `banner-${monthKey}`,
          label: formatBannerLabel(date),
        });

        const leadingEmpties = (dayOfWeek + DAYS_PER_WEEK - 1) % DAYS_PER_WEEK;
        for (let j = 0; j < leadingEmpties; j++) {
          items.push({
            kind: "empty",
            key: `empty-${monthKey}-${j}`,
          });
        }

        lastMonthKey = monthKey;
      }

      items.push({
        kind: "day",
        key: `day-${dateKey}`,
        date,
        day: date.getDate(),
        isDisabled,
        isSelected,
        hasSlots,
      });
    }

    return items;
  }, [
    numberOfDays,
    selectedDate,
    studioHours,
    hasRealData,
    hasAvailableSlots,
  ]);
};
